import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { roundToDecimalPlaces } from '@moment-golf/shared';
import type { challengeSubmission, EventScoreWithId, TbRegistration } from '@moment-golf/types';
import CreateApi from '../../../../shared/Settings/appSettings';
import {
    type ChallengeGroupPathEntry,
    getPlayersWithoutChallengeSubmission,
    resolvePlayerEventScore,
    upsertChallengeSubmissionOnScore,
} from '../../../utils/challengeCommandCenterHelpers';
import type { ChallengeConfigRow } from './AddChallengeResultDialog';

interface ChallengeRandomDrawDialogProps {
    open: boolean;
    onClose: () => void;
    challenge: ChallengeConfigRow;
    roundNumber: number;
    registrations: TbRegistration[];
    scores: EventScoreWithId[];
    groupPath?: ChallengeGroupPathEntry[];
    onSaved: (updatedScores: EventScoreWithId[]) => void;
}

const SPIN_MS = 2000;
const TICK_MS = 60;

function pickRandomIndex(length: number): number {
    if (length <= 0) return 0;
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        const buf = new Uint32Array(1);
        crypto.getRandomValues(buf);
        return buf[0] % length;
    }
    return Math.floor(Math.random() * length);
}

export const ChallengeRandomDrawDialog = ({
    open,
    onClose,
    challenge,
    roundNumber,
    registrations,
    scores,
    groupPath,
    onSaved,
}: ChallengeRandomDrawDialogProps) => {
    const auth = useAuth();
    const api = CreateApi();

    const challengeName =
        challenge.name != null && String(challenge.name).trim() !== ''
            ? String(challenge.name)
            : String(challenge.type ?? '');
    const challengeHole = Number(challenge.hole);

    const eligible = useMemo(
        () =>
            getPlayersWithoutChallengeSubmission(
                registrations,
                scores,
                roundNumber,
                challengeName,
                challengeHole,
                groupPath,
            ),
        [registrations, scores, roundNumber, challengeName, challengeHole, groupPath],
    );

    const [displayName, setDisplayName] = useState<string>('—');
    const [spinning, setSpinning] = useState(false);
    const [winner, setWinner] = useState<string | null>(null);
    const [logging, setLogging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = () => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
        if (stopRef.current) {
            clearTimeout(stopRef.current);
            stopRef.current = null;
        }
    };

    useEffect(() => {
        if (!open) {
            clearTimers();
            return;
        }
        setDisplayName('—');
        setSpinning(false);
        setWinner(null);
        setLogging(false);
        setError(null);
        return clearTimers;
    }, [open, challengeName, challengeHole, groupPath]);

    const ruleLines = useMemo(() => {
        if (!groupPath?.length) {
            return ['All registered players (no division / flight / gender / age filter)'];
        }
        return groupPath.map((entry) => {
            const dim =
                entry.dimension.charAt(0).toUpperCase() + entry.dimension.slice(1);
            return `${dim}: ${entry.label}`;
        });
    }, [groupPath]);

    const handleRun = () => {
        if (eligible.length === 0 || spinning) return;
        setError(null);
        setWinner(null);
        setSpinning(true);

        clearTimers();
        tickRef.current = setInterval(() => {
            const idx = pickRandomIndex(eligible.length);
            setDisplayName(eligible[idx].label);
        }, TICK_MS);

        stopRef.current = setTimeout(() => {
            clearTimers();
            const finalIdx = pickRandomIndex(eligible.length);
            const name = eligible[finalIdx].label;
            setDisplayName(name);
            setWinner(name);
            setSpinning(false);
        }, SPIN_MS);
    };

    const handleLogSubmission = async () => {
        if (!winner) return;

        const resolved = resolvePlayerEventScore(winner, registrations, scores, roundNumber);
        if (!resolved) {
            setError('No event score found for this player');
            return;
        }

        const decimalPlaces = Number(challenge.decimalPlaces);
        const value =
            challenge.inputType === 'number'
                ? roundToDecimalPlaces(1, Number.isFinite(decimalPlaces) ? decimalPlaces : 1)
                : true;

        const submission: challengeSubmission = {
            challengeName,
            challengeHole,
            playerName: winner,
            value,
            submittedAt: new Date(),
            submittedBy: auth.user?.profile.sub ?? 'commandCenter',
        };

        const updatedScores: EventScoreWithId[] = JSON.parse(JSON.stringify(scores));
        const scoreIdx = updatedScores.findIndex((s) => String(s._id) === resolved.eventScoreID);
        if (scoreIdx < 0) {
            setError('Event score document not found');
            return;
        }

        const updatedChallenges = upsertChallengeSubmissionOnScore(
            updatedScores[scoreIdx],
            resolved.roundIndex,
            submission,
        );

        setLogging(true);
        setError(null);
        try {
            await api.post('/ls/UpdateChallengeScores', {
                eventScoreID: resolved.eventScoreID,
                roundIndex: resolved.roundIndex,
                challengeSubmissions: updatedChallenges,
                userID: auth.user?.profile.sub ?? 'commandCenter',
            });
            onSaved(updatedScores);
            onClose();
        } catch (err) {
            console.error('Error logging challenge submission', err);
            setError('Failed to log submission');
        } finally {
            setLogging(false);
        }
    };

    return (
        <Dialog open={open} onClose={spinning ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>Random Player Draw</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {challengeName} — Hole {challengeHole}
                    </Typography>
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Selection rules
                        </Typography>
                        {ruleLines.map((line) => (
                            <Typography key={line} variant="body2">
                                {line}
                            </Typography>
                        ))}
                        <Typography variant="body2" sx={{ pt: 1 }}>
                            Eligible players: <strong>{eligible.length}</strong>
                        </Typography>
                    </Stack>
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: '100%',
                            minHeight: 72,
                            border: '1px solid #E0E0E0',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            px: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                            }}
                        >
                            {displayName}
                        </Typography>
                    </Stack>
                    {error ? (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button onClick={onClose} disabled={spinning || logging}>
                    Close
                </Button>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        onClick={handleRun}
                        disabled={spinning || logging || eligible.length === 0}
                    >
                        {spinning ? 'Spinning…' : 'Run'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleLogSubmission}
                        disabled={!winner || spinning || logging}
                    >
                        Log submission
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
